import { useEffect } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useSessionStore } from '@/stores/useSessionStore'
import { useMe } from '@/features/auth/api'
import {
  isInitialPasswordSetupDeadlinePassed,
  needsInitialPasswordSetupRoute,
} from '@/features/auth/lib/initial-password-setup'
import {
  AuthBootstrapSpinner,
  AuthRedirectWhenUnauthenticated,
} from './requireAuthRouting'

export function RequireAuth() {
  const {
    isAuthenticated,
    user,
    explicitLogout,
    clearSession,
    passwordSetupRequired,
    passwordSetupDeadlineMs,
    pendingInitialPasswordSetup,
    requiresForceReset,
  } = useSessionStore()
  const location = useLocation()
  const { isLoading, isPending, isError } = useMe()

  const mustSetPassword =
    passwordSetupRequired ||
    needsInitialPasswordSetupRoute(passwordSetupDeadlineMs)

  // Signup verify seeds Zustand before cookies hydrate; allow home until TTL.
  const signupPasswordGracePeriod =
    pendingInitialPasswordSetup &&
    isAuthenticated &&
    !!user &&
    !isInitialPasswordSetupDeadlinePassed(passwordSetupDeadlineMs)

  useEffect(() => {
    // Cookie hydration failed — drop any body-only Zustand session so refresh
    // cannot leave the user "logged in" until the next hard reload.
    // Exception: deferred password setup after signup verify (home until deadline).
    if (isError && !mustSetPassword && !signupPasswordGracePeriod) {
      clearSession()
    }
  }, [isError, clearSession, mustSetPassword, signupPasswordGracePeriod])

  if (isError && !mustSetPassword && !signupPasswordGracePeriod) {
    return (
      <AuthRedirectWhenUnauthenticated mustSetPassword={mustSetPassword} />
    )
  }

  if (
    !explicitLogout &&
    !isAuthenticated &&
    !user &&
    (isLoading || isPending)
  ) {
    return <AuthBootstrapSpinner />
  }

  if (!isAuthenticated || !user) {
    return (
      <AuthRedirectWhenUnauthenticated mustSetPassword={mustSetPassword} />
    )
  }

  if (requiresForceReset) {
    if (location.pathname !== '/force-reset/password') {
      return <Navigate to="/force-reset/password" replace />
    }
    return <Outlet />
  }

  if (passwordSetupRequired && location.pathname !== '/set-password') {
    return <Navigate to="/set-password" replace />
  }

  return <Outlet />
}
